import React, { useState, useRef, useEffect } from 'react';
import { useBreakpointValue } from '@chakra-ui/media-query';
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Flex,
  Button,
  Stack,
} from '@chakra-ui/react';

const isSelfOrChildSelected = (category, selectedId) =>
  category.id === selectedId ||
  category.children?.some((child) => child.id === selectedId);

export type CategorySelectorProps = {
  categories: any[];
  currentCategoryId: string;
  onSelect: React.Dispatch<React.SetStateAction<string>>;
};

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  categories = [],
  currentCategoryId = null,
  onSelect = () => null,
}) => {
  const element = useRef<HTMLDivElement>();

  const [isOverflown, setIsOverflown] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsOverflown(
        element.current?.scrollWidth > element.current?.clientWidth
      );
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const categoryPopoverTrigger: 'click' | 'hover' = useBreakpointValue({
    base: 'click',
    md: 'hover',
  });

  return (
    <Flex
      minW={0}
      maxW="full"
      overflow="auto"
      marginRight="25px"
      sx={{
        '::-webkit-scrollbar': {
          height: '5px',
        },
        '::-webkit-scrollbar-track': {
          background: 'scheme.bgPrimary',
        },
        '::-webkit-scrollbar-thumb': {
          background: 'scheme.bgSecondary',
        },
      }}
      flexShrink={1}
      position="relative"
      _after={
        isOverflown
          ? {
              content: `""`,
              position: 'absolute',
              height: 'full',
              bgGradient:
                'linear(to-l, scheme.bgPrimary, rgba(255,255,255,0.1))',
              top: 0,
              right: 0,
              width: '25px',
              pointerEvents: 'none',
            }
          : undefined
      }
    >
      <Flex
        flexShrink={1}
        overflowX={['scroll', 'initial', 'initial']}
        overflowY="visible"
        ref={element}
        _after={
          isOverflown
            ? {
                content: `""`,
                minWidth: '25px',
                pointerEvents: 'none',
              }
            : undefined
        }
      >
        <Button
          variant="link"
          color="gray.800"
          textDecoration={currentCategoryId === null ? 'underline' : 'none'}
          my={4}
          onClick={() => onSelect(null)}
          flexShrink={0}
        >
          All items
        </Button>
        {categories.map((category) => (
          <Popover
            trigger={categoryPopoverTrigger}
            key={category.id}
            variant="OD-tooltip"
            placement="bottom-start"
            isLazy
          >
            <PopoverTrigger>
              <Button
                my={4}
                color="gray.800"
                mx={2}
                variant="link"
                onClick={() => onSelect(category.id)}
                textDecoration={
                  isSelfOrChildSelected(category, currentCategoryId)
                    ? 'underline'
                    : 'none'
                }
                flexShrink={0}
              >
                {category.name}
              </Button>
            </PopoverTrigger>
            {!!category.children.length && (
              <Portal>
                <PopoverContent ml={-2}>
                  <PopoverBody
                    as={Stack}
                    alignItems="flex-start"
                    flex={1}
                    p={4}
                    spacing={4}
                  >
                    {category.children.map((child) => (
                      <Button
                        variant="link"
                        color="gray.800"
                        key={child.id}
                        onClick={() => onSelect(child.id)}
                        textDecoration={
                          isSelfOrChildSelected(child, currentCategoryId)
                            ? 'underline'
                            : 'none'
                        }
                      >
                        {child.name}
                      </Button>
                    ))}
                  </PopoverBody>
                </PopoverContent>
              </Portal>
            )}
          </Popover>
        ))}
      </Flex>
    </Flex>
  );
};
